import { onBeforeUnmount, onMounted, Ref } from 'vue';

const copyInlineComputedStyles = (original: HTMLElement, cloned: HTMLElement) => {
  const computed = getComputedStyle(original);
  for (let i = 0; i < computed.length; i++) {
    const prop = computed[i];
    cloned.style.setProperty(prop, computed.getPropertyValue(prop));
  }
};

const copyComputedStyles = (original: Element, cloned: Element) => {
  for (let i = 0; i < original.children.length; i++) {
    const child = original.children[i];
    const clonedChild = cloned.children[i];
    if (!(child instanceof HTMLElement) || !(clonedChild instanceof HTMLElement)) continue;
    copyInlineComputedStyles(child, clonedChild);
    if (child.children.length > 0) {
      copyComputedStyles(child, clonedChild);
    }
  }
};

interface UseBaseMenuDragOptions {
  containerRef: Ref<HTMLElement | null>;
  draggableSelector: string;
  longPressDelay?: number;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onReorder: (oldIndex: number, newIndex: number) => void;
}

export const useBaseMenuDrag = ({
  containerRef,
  draggableSelector,
  longPressDelay = 300,
  onDragStart,
  onDragEnd,
  onReorder,
}: UseBaseMenuDragOptions) => {
  const CLICK_THRESHOLD = 5;
  const EDGE_ZONE = 40;
  const MAX_SCROLL_SPEED = 15;

  let isDragging = false;
  let draggedIndex = -1;
  let insertAtIndex = -1;
  let draggedEl: HTMLElement | null = null;
  let ghostEl: HTMLElement | null = null;
  let indicatorEl: HTMLElement | null = null;
  let cursorStyleEl: HTMLStyleElement | null = null;
  let pressTimer: number | null = null;
  let pressStartX = 0;
  let pressStartY = 0;
  let offsetX = 0;
  let offsetY = 0;
  let lastClientX = 0;
  let lastClientY = 0;
  let autoScrollRAF: number | null = null;
  let attachedEl: HTMLElement | null = null;

  const getRows = (): HTMLElement[] => {
    if (!containerRef.value) return [];
    return Array.from(containerRef.value.querySelectorAll<HTMLElement>(draggableSelector));
  };

  const getVisibleRows = (): HTMLElement[] =>
    getRows().filter((row) => row.style.display !== 'none' && row.offsetParent !== null);

  const createGhost = (li: HTMLElement) => {
    // 서브메뉴(folding-sub-menu)가 펼쳐진 상태(.opened)면 li 전체를, 아니면 .menu-element__title만 ghost로 사용.
    const folding = li.querySelector<HTMLElement>('.folding-sub-menu');
    const subMenuOpened = folding?.classList.contains('opened') ?? false;
    const sourceEl = subMenuOpened
      ? li
      : (li.querySelector<HTMLElement>('.menu-element__title') ?? li);
    const rect = sourceEl.getBoundingClientRect();
    // ghost source가 바뀌면 offset도 그 기준으로 재계산.
    offsetX = lastClientX - rect.left;
    offsetY = lastClientY - rect.top;

    const cloned = sourceEl.cloneNode(true);
    if (!(cloned instanceof HTMLElement)) return;
    const clone = cloned;
    copyInlineComputedStyles(sourceEl, clone);
    copyComputedStyles(sourceEl, clone);
    clone.style.position = 'fixed';
    clone.style.pointerEvents = 'none';
    clone.style.zIndex = '9999';
    clone.style.margin = '0';
    clone.style.width = `${rect.width}px`;
    clone.style.height = `${rect.height}px`;
    clone.style.backgroundColor = 'var(--color-gray-00-12)';
    clone.style.opacity = '0.6';
    clone.style.boxShadow = '0 2px 6px 0 rgba(0, 13, 29, 0.15)';
    clone.style.borderRadius = '4px';
    document.body.appendChild(clone);
    ghostEl = clone;
  };

  const createIndicator = () => {
    if (!containerRef.value) return;
    // wrapper의 overflow:hidden에 의해 도트가 잘리지 않도록 document.body에 fixed로 띄운다.
    const indicator = document.createElement('div');
    indicator.style.cssText =
      'position:fixed;height:2px;pointer-events:none;z-index:9999;display:none;';
    indicator.style.backgroundColor = 'var(--color-blue-05-05)';

    // 왼쪽 끝의 6x6 도트(외곽선 only) — indicator 라인 좌측 끝 바로 옆에 붙여 라인이
    // 도트 우측 끝부터 시작하도록 한다(도트 내부는 비어있으므로 라인이 가로지르면 어색함).
    const dot = document.createElement('div');
    dot.style.cssText =
      'position:absolute;left:-6px;top:50%;width:6px;height:6px;border-radius:50%;border-width:2px;border-style:solid;box-sizing:border-box;background-color:transparent;';
    dot.style.transform = 'translateY(-50%)';
    dot.style.borderColor = 'var(--color-blue-05-05)';
    indicator.appendChild(dot);

    document.body.appendChild(indicator);
    indicatorEl = indicator;
  };

  const updateGhostPosition = (clientX: number, clientY: number) => {
    if (!ghostEl) return;
    ghostEl.style.left = `${clientX - offsetX}px`;
    ghostEl.style.top = `${clientY - offsetY}px`;
  };

  const updateIndicatorPosition = (clientX: number, clientY: number) => {
    if (!indicatorEl || !containerRef.value) return;

    const visibleRows = getVisibleRows();
    if (visibleRows.length === 0) {
      indicatorEl.style.display = 'none';
      return;
    }

    const wrapperRect = containerRef.value.getBoundingClientRect();
    const isInDropZone =
      clientX >= wrapperRect.left &&
      clientX <= wrapperRect.right &&
      clientY >= wrapperRect.top &&
      clientY <= wrapperRect.bottom;

    if (!isInDropZone) {
      indicatorEl.style.display = 'none';
      insertAtIndex = -1;
      return;
    }

    let targetIndex = visibleRows.length;

    for (let i = 0; i < visibleRows.length; i += 1) {
      const rect = visibleRows[i].getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      if (clientY < midY) {
        targetIndex = i;
        break;
      }
    }

    // indicator는 fixed로 띄워져 있으므로 viewport 좌표(getBoundingClientRect)를 그대로 사용.
    let indicatorTop: number;
    if (targetIndex === 0) {
      indicatorTop = visibleRows[0].getBoundingClientRect().top;
    } else if (targetIndex >= visibleRows.length) {
      indicatorTop = visibleRows[visibleRows.length - 1].getBoundingClientRect().bottom;
    } else {
      indicatorTop = visibleRows[targetIndex].getBoundingClientRect().top;
    }

    // 좌우 inset은 메뉴 li(첫 visible row) 기준으로 맞춘다. 도트(6×6, dot.left=-6)가
    // 라인 좌측 외부에 붙어 있으므로, 도트 좌측 끝이 메뉴 li 좌측과 일치하도록 라인 자체는
    // 6px 안쪽으로 들이고 width도 그만큼 줄인다.
    const sampleRect = visibleRows[0].getBoundingClientRect();
    indicatorEl.style.left = `${sampleRect.left + 6}px`;
    indicatorEl.style.width = `${sampleRect.width - 6}px`;
    indicatorEl.style.top = `${indicatorTop}px`;
    indicatorEl.style.display = 'block';

    const allRows = getRows();
    if (targetIndex >= visibleRows.length) {
      const lastVisibleRow = visibleRows[visibleRows.length - 1];
      const originalIndex = allRows.indexOf(lastVisibleRow);
      insertAtIndex = originalIndex >= draggedIndex ? originalIndex : originalIndex + 1;
    } else {
      const targetRow = visibleRows[targetIndex];
      const originalIndex = allRows.indexOf(targetRow);
      insertAtIndex = originalIndex > draggedIndex ? originalIndex - 1 : originalIndex;
    }
  };

  const stopAutoScroll = () => {
    if (autoScrollRAF !== null) {
      cancelAnimationFrame(autoScrollRAF);
      autoScrollRAF = null;
    }
  };

  const autoScrollTick = () => {
    if (!isDragging || !containerRef.value) {
      autoScrollRAF = null;
      return;
    }
    const wrapper = containerRef.value;
    if (wrapper.scrollHeight <= wrapper.clientHeight) {
      autoScrollRAF = null;
      return;
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    const distFromTop = lastClientY - wrapperRect.top;
    const distFromBottom = wrapperRect.bottom - lastClientY;
    let scrollDelta = 0;

    if (distFromTop < EDGE_ZONE && distFromTop >= 0) {
      const intensity = 1 - distFromTop / EDGE_ZONE;
      scrollDelta = -Math.ceil(MAX_SCROLL_SPEED * intensity);
    } else if (distFromBottom < EDGE_ZONE && distFromBottom >= 0) {
      const intensity = 1 - distFromBottom / EDGE_ZONE;
      scrollDelta = Math.ceil(MAX_SCROLL_SPEED * intensity);
    }

    if (scrollDelta !== 0) {
      wrapper.scrollTop += scrollDelta;
      updateIndicatorPosition(lastClientX, lastClientY);
      autoScrollRAF = requestAnimationFrame(autoScrollTick);
    } else {
      autoScrollRAF = null;
    }
  };

  const startAutoScroll = () => {
    if (autoScrollRAF !== null) return;
    autoScrollRAF = requestAnimationFrame(autoScrollTick);
  };

  const onWheel = (e: WheelEvent) => {
    if (!isDragging || !containerRef.value) return;
    e.preventDefault();
    containerRef.value.scrollTop += e.deltaY;
    updateIndicatorPosition(lastClientX, lastClientY);
  };

  const onDragMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    lastClientX = e.clientX;
    lastClientY = e.clientY;
    updateGhostPosition(e.clientX, e.clientY);
    updateIndicatorPosition(e.clientX, e.clientY);
    startAutoScroll();
  };

  const onDragMouseUp = (e: MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    const oldIndex = draggedIndex;
    const newIndex = insertAtIndex;
    cleanup();
    if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      onReorder(oldIndex, newIndex);
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isDragging) {
      cleanup();
    }
  };

  function cleanup() {
    if (ghostEl) {
      ghostEl.remove();
      ghostEl = null;
    }
    if (indicatorEl) {
      indicatorEl.remove();
      indicatorEl = null;
    }
    if (draggedEl) {
      draggedEl.style.display = '';
      draggedEl = null;
    }
    if (cursorStyleEl) {
      cursorStyleEl.remove();
      cursorStyleEl = null;
    }
    document.body.style.userSelect = '';
    stopAutoScroll();

    document.removeEventListener('mousemove', onDragMouseMove);
    document.removeEventListener('mouseup', onDragMouseUp);
    document.removeEventListener('keydown', onKeyDown);
    document.removeEventListener('wheel', onWheel, { capture: true });

    const wasDragging = isDragging;
    isDragging = false;
    draggedIndex = -1;
    insertAtIndex = -1;
    lastClientX = 0;
    lastClientY = 0;

    if (wasDragging) {
      onDragEnd?.();
    }
  }

  const activate = (li: HTMLElement) => {
    draggedEl = li;
    createGhost(li);
    updateGhostPosition(lastClientX, lastClientY);

    li.style.display = 'none';
    document.body.style.userSelect = 'none';

    cursorStyleEl = document.createElement('style');
    cursorStyleEl.textContent = '* { cursor: grabbing !important; }';
    document.head.appendChild(cursorStyleEl);

    createIndicator();
    updateIndicatorPosition(lastClientX, lastClientY);

    isDragging = true;

    document.addEventListener('mousemove', onDragMouseMove);
    document.addEventListener('mouseup', onDragMouseUp);
    document.addEventListener('keydown', onKeyDown);
    // document를 타겟으로 하는 wheel 리스너는 passive 기본값이 true라 onWheel 내부의
    // preventDefault()가 silently 무시된다(콘솔 인터벤션 경고 + default scroll이 그대로 발생).
    // 드래그 중에는 메뉴 내부 스크롤만 적용되어야 하므로 passive: false로 명시한다.
    document.addEventListener('wheel', onWheel, { capture: true, passive: false });

    onDragStart?.();
  };

  const onPressMouseMove = (e: MouseEvent) => {
    const dx = e.clientX - pressStartX;
    const dy = e.clientY - pressStartY;
    if (Math.sqrt(dx * dx + dy * dy) > CLICK_THRESHOLD) {
      clearPressTimer();
    }
  };

  const onPressMouseUp = () => {
    clearPressTimer();
  };

  function clearPressTimer() {
    if (pressTimer !== null) {
      clearTimeout(pressTimer);
      pressTimer = null;
    }
    document.removeEventListener('mousemove', onPressMouseMove);
    document.removeEventListener('mouseup', onPressMouseUp);
  }

  const onMouseDown = (e: MouseEvent) => {
    if (e.button !== 0) return;
    if (!(e.target instanceof Element)) return;
    const li = e.target.closest<HTMLElement>(draggableSelector);
    if (!li || !containerRef.value || !containerRef.value.contains(li)) return;

    const allRows = getRows();
    const idx = allRows.indexOf(li);
    if (idx === -1) return;

    const rect = li.getBoundingClientRect();
    pressStartX = e.clientX;
    pressStartY = e.clientY;
    offsetX = e.clientX - rect.left;
    offsetY = e.clientY - rect.top;
    lastClientX = e.clientX;
    lastClientY = e.clientY;
    draggedIndex = idx;

    document.addEventListener('mousemove', onPressMouseMove);
    document.addEventListener('mouseup', onPressMouseUp);

    pressTimer = window.setTimeout(() => {
      pressTimer = null;
      document.removeEventListener('mousemove', onPressMouseMove);
      document.removeEventListener('mouseup', onPressMouseUp);
      activate(li);
    }, longPressDelay);
  };

  const attach = (el: HTMLElement | null) => {
    if (attachedEl === el) return;
    if (attachedEl) {
      attachedEl.removeEventListener('mousedown', onMouseDown);
    }
    attachedEl = el;
    if (el) {
      el.addEventListener('mousedown', onMouseDown);
    }
  };

  onMounted(() => {
    attach(containerRef.value);
  });

  onBeforeUnmount(() => {
    clearPressTimer();
    cleanup();
    attach(null);
  });
};
